import axios from "axios";
import { logError, shouldLogStatus } from "~/lib/logger";
import AuthService from "~/services/authentication/auth";
import { ENV } from "./env.config";
import { useAuthStore } from "~/store/authentication/useAuthStore";
import { useSudoStore } from "~/store/sudo/useSudoStore";
import { hasPendingLogout } from "~/lib/auth/pending-logout-marker";
import {
  classifyServiceOutage,
  configureServiceAvailabilityProbe,
  issueRequestSeq,
  recordServiceAvailable,
  recordServiceUnavailable,
  resetServiceAvailability,
  retryAfterMsOf,
} from "~/lib/service-availability";

declare module "axios" {
  interface AxiosRequestConfig {
    /**
     * Statuses this request treats as an ordinary answer rather than a fault — a route a backend
     * may not have yet answers 404, and the caller has a fallback for it. The response
     * interceptor does not report them to Sentry. Anything else still does.
     */
    expectedStatuses?: readonly number[];
  }
}

const BASE_URL = ENV.API_BASE_URL;

// console.log("BASE_URL", BASE_URL);

function createAxiosInstance() {
  return axios.create({
    baseURL: BASE_URL,
    withCredentials: true,
    timeout: 45000, // 45 seconds
    headers: {
      common: {
        Accept: "application/json",
      },
      post: {
        "Content-Type": "application/json",
      },
    },
  });
}

export const axiosInstance = createAxiosInstance();
export const axiosPrivate = createAxiosInstance();

// ---------- CSRF (DEV-FA7) ----------
// The backend issues a JS-readable `palate_csrf` cookie alongside the session
// (double-submit pattern). For state-changing requests we echo that cookie
// value back in the `X-CSRF-Token` header; the backend's CsrfMiddleware rejects
// mutations whose header doesn't match the session's synchronizer token. Safe
// methods (GET/HEAD/OPTIONS) carry no header. Reading the cookie per-request
// (not once at boot) means a re-login that rotates the token is picked up
// immediately.
const CSRF_COOKIE_NAME = "palate_csrf";
const MUTATING_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

export function readCsrfToken(): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(
    new RegExp(`(?:^|;\\s*)${CSRF_COOKIE_NAME}=([^;]*)`),
  );
  return match ? decodeURIComponent(match[1]) : null;
}

function attachCsrfHeader(request: {
  method?: string;
  headers: Record<string, unknown> & { [k: string]: unknown };
}) {
  const method = request.method?.toUpperCase();
  if (!method || !MUTATING_METHODS.has(method)) return;
  const token = readCsrfToken();
  if (token) {
    request.headers["X-CSRF-Token"] = token;
  }
}

type RefreshResult = Awaited<ReturnType<typeof AuthService.refreshSession>>;

// Single-flight refresh: when multiple callers (proactive timer, 401 interceptor,
// app-init) need a fresh token at the same time, they share one in-flight request
// instead of racing — which would otherwise consume each other's rotating refresh
// tokens and produce intermittent 401s.
//
// This guard is PER-TAB only (module-scope state doesn't cross tabs). Cross-tab
// exclusion is handled separately below via the Web Locks API — see
// performCrossTabRefresh / CROSS_TAB_REFRESH_LOCK.
let inflightRefresh: Promise<RefreshResult> | null = null;

// ---------- Proactive Token Refresh Timer ----------
let refreshTimer: ReturnType<typeof setTimeout> | null = null;

export function clearTokenRefreshTimer() {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
  // Every caller of this — sign-out, a dead session, another tab taking
  // over, a fresh token pair being scheduled — also ends any background
  // retry of a failed refresh (GH-924). Left pending across a sign-out, that
  // retry would find no stored pair and fall back to the HttpOnly refresh
  // cookie, which is still live whenever the logout request itself failed —
  // signing the previous user straight back in on a shared back-office PC.
  clearTransientRefreshRetry();
}

// ---------- Cross-tab refresh (backend#1854 reuse detection) ----------
//
// Backend#1854 rotates the refresh token on every /v1/auth/refresh call and
// revokes the WHOLE session family (every tab, every device) if an
// already-rotated token is replayed more than 45s after its rotation. Before
// this, each tab's in-flight guard above only deduped callers WITHIN that
// tab — the refresh token itself lived in this tab's in-memory zustand
// state. Tab A refreshes and rotates; tab B still holds the old token in
// memory; when B's own proactive timer later fires, it replays that stale
// token straight into a session-wide revocation, logging every tab out.
//
// The fix has two parts:
//   1. `AUTH_STORAGE_KEY` — read the CURRENT pair from the persisted store
//      (localStorage, written by every tab's zustand `persist` middleware)
//      instead of this tab's memory. That fixes the replay: the persisted
//      value is the latest rotation, from whichever tab produced it —
//      except when a stale tab's write sent it backwards, which
//      isOlderRotation detects and the newer tab repairs.
//   2. `CROSS_TAB_REFRESH_LOCK` — serialize the refresh itself across every
//      tab via the Web Locks API, so two tabs can't both decide "I need to
//      refresh" from the same still-current token at the same instant and
//      race each other into a double rotation.
//
// Two rules keep that sound once the `storage` listener below is also
// syncing tokens into memory:
//   - Adopt-vs-refresh is decided against the token the CALLER held (the one
//     that 401'd, or the one the proactive timer was scheduled for) — never
//     against this tab's memory at lock time, which the listener has usually
//     already overwritten with the other tab's rotation. Comparing against
//     memory there made every concurrent refresh rotate again, and because
//     the backend only accepts a session's CURRENT access token, each extra
//     rotation 401'd the other tabs' in-flight requests, which rotated again.
//   - Only a rotation of THIS tab's session is pulled in place (token pair
//     only — each tab keeps its own selected location and profile). A
//     different session in storage (logout, another user signing in, an
//     impersonation handoff) supersedes this tab: it detaches from shared
//     storage and reloads, so no cached data or in-flight request from one
//     identity ever runs under another.
const AUTH_STORAGE_KEY = "palate-auth";
const CROSS_TAB_REFRESH_LOCK = "palate-auth-refresh";
// Buffer below which an adopted (persisted) access token isn't worth
// adopting — the network round-trip to refresh it is safer than handing a
// caller a token that may expire before it reaches the server. Small on
// purpose: scheduleTokenRefresh's own 10s floor already covers "adopted a
// short-lived token, refresh again almost immediately".
const MIN_ADOPTABLE_TOKEN_LIFETIME_SECONDS = 5;

type PersistedAuthPair = { token: string | null; refreshToken: string | null };

/**
 * Reads the access/refresh token pair directly from the persisted zustand
 * store (localStorage[AUTH_STORAGE_KEY]) rather than this tab's in-memory
 * `useAuthStore` state — see "Cross-tab refresh" above for why that
 * distinction is the actual fix. Zustand's persist middleware writes
 * `{ state: {...partializedState} }` (node_modules/zustand/esm/middleware.mjs
 * `setItem`). Defensive against a missing/corrupt entry (private browsing,
 * storage quota, manual edit, or no session yet).
 */
function readPersistedAuthPair(): PersistedAuthPair | null {
  if (typeof localStorage === "undefined") return null;
  try {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { state?: Partial<PersistedAuthPair> };
    const state = parsed?.state;
    if (!state) return null;
    return {
      token: typeof state.token === "string" ? state.token : null,
      refreshToken:
        typeof state.refreshToken === "string" ? state.refreshToken : null,
    };
  } catch {
    return null;
  }
}

/** The JWT's payload claims, or null when `token` isn't a decodable JWT. */
function decodeJwtClaims(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const payload: unknown = JSON.parse(atob(base64));
    return payload && typeof payload === "object"
      ? (payload as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

/**
 * Seconds remaining until `token`'s `exp` claim, or null if the token can't
 * be decoded or carries no `exp`. Only used to decide whether a token
 * adopted from another tab (performCrossTabRefresh) is still worth adopting.
 */
function decodeJwtExpirySeconds(token: string): number | null {
  const exp = decodeJwtClaims(token)?.exp;
  if (typeof exp !== "number") return null;
  return exp - Math.floor(Date.now() / 1000);
}

/**
 * The backend session an access token belongs to — `sub` + `sid` for a
 * staff session (a refresh re-signs both unchanged), the impersonation
 * session id for an Ops Panel handoff. Null when the token can't be decoded.
 */
function sessionKeyOf(token: string): string | null {
  const claims = decodeJwtClaims(token);
  if (!claims || typeof claims.sub !== "string") return null;
  if (claims.type === "impersonation") {
    const impersonation = claims.impersonation as
      | { sessionId?: unknown }
      | undefined;
    return typeof impersonation?.sessionId === "string"
      ? `impersonation:${impersonation.sessionId}`
      : null;
  }
  const sid =
    typeof claims.sid === "string"
      ? claims.sid
      : typeof claims.jti === "string"
        ? claims.jti
        : null;
  return sid ? `${claims.sub}:${sid}` : null;
}

/**
 * True when `a` and `b` are the same backend session (a rotation), false when
 * the session changed underneath this tab — signed in vs signed out, another
 * user, a new login, an impersonation handoff. A token that can't be decoded
 * can't prove a switch, so it's treated as a rotation (in-place adoption).
 */
function isSameSession(a: string | null, b: string | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  const keyA = sessionKeyOf(a);
  const keyB = sessionKeyOf(b);
  if (keyA === null || keyB === null) return true;
  return keyA === keyB;
}

/**
 * True only when `candidate` was provably issued before `current` — an older
 * rotation of the same session. Storage isn't monotonic: zustand `persist`
 * writes a tab's WHOLE state, token pair included, on every store update, so
 * a tab that hasn't caught up yet (restored from the back/forward cache, or a
 * write that crossed another tab's rotation in flight) persists the pair it
 * holds over a newer one. That older pair's refresh token has already been
 * rotated away — sending it more than 45s later is the replay backend#1854
 * revokes every session for. Equal or missing `iat` can't prove anything and
 * is not "older".
 */
function isOlderRotation(
  candidate: string | null,
  current: string | null,
): boolean {
  if (!candidate || !current || candidate === current) return false;
  const candidateIat = decodeJwtClaims(candidate)?.iat;
  const currentIat = decodeJwtClaims(current)?.iat;
  return (
    typeof candidateIat === "number" &&
    typeof currentIat === "number" &&
    candidateIat < currentIat
  );
}

/** Thrown to refresh callers once this tab's session has been replaced — by
 *  another tab, or by a sign-out/sign-in here while the refresh was in
 *  flight (doNetworkRefresh). Callers stop quietly: no retry, no logout. */
export class SessionSupersededError extends Error {
  constructor(
    message = "The session was changed in another tab — reloading.",
  ) {
    super(message);
    this.name = "SessionSupersededError";
  }
}

/**
 * #931 — thrown to refresh callers while a sign-out is still unconfirmed by
 * the server (see ~/lib/auth/pending-logout-marker). With no session in this
 * tab, a refresh could only run off the HttpOnly refresh cookie — the live
 * cookie of the session that was just signed out — and sign that user
 * straight back in. Callers stop quietly: no retry, no logout.
 */
export class PendingLogoutError extends Error {
  constructor() {
    super(
      "A sign-out is still being confirmed with the server — not refreshing the signed-out session.",
    );
    this.name = "PendingLogoutError";
  }
}

/** True when a refresh now could only resurrect a signed-out session. */
function isRefreshBlockedByPendingLogout(): boolean {
  return hasPendingLogout() && !useAuthStore.getState().token;
}

/** Thrown to refresh callers in a tab running an Ops Panel impersonation session. */
export class ImpersonationSessionNotRefreshableError extends Error {
  constructor() {
    super(
      "Support-mode (impersonation) sessions have no refresh token — they expire on their own; exit and re-enter from the Ops Panel.",
    );
    this.name = "ImpersonationSessionNotRefreshableError";
  }
}

// Set once this tab's session has been replaced by another tab's. From then on
// this tab never refreshes and never clears or writes the shared auth store —
// it is only waiting for its own reload.
let sessionSupersededByAnotherTab = false;

// Swallows every write. Swapped in for localStorage once this tab is
// superseded: a late /me → setProfile, or any other store write between now
// and unload, would otherwise persist THIS tab's old identity over the
// session the other tab just stored — and every other tab would follow it.
const DETACHED_AUTH_STORAGE = {
  getItem: () => null,
  setItem: () => {},
  removeItem: () => {},
};

/**
 * Another tab signed out, signed in as someone else, or started an
 * impersonation session. Don't pull that into this tab's memory: its React
 * Query cache, open forms and in-flight requests all belong to the previous
 * identity. Detach from shared storage and reload — the fresh page boots from
 * whatever session storage now holds (or lands on /login when it holds none).
 */
function supersedeTabSession(nextToken: string | null) {
  if (sessionSupersededByAnotherTab) return;
  sessionSupersededByAnotherTab = true;
  clearTokenRefreshTimer();
  useAuthStore.persist.setOptions({ storage: DETACHED_AUTH_STORAGE });

  if (typeof window === "undefined") return;
  if (nextToken) {
    window.location.reload();
  } else {
    window.location.assign("/login");
  }
}

function hasWebLocks(): boolean {
  return (
    typeof navigator !== "undefined" &&
    typeof navigator.locks !== "undefined" &&
    typeof navigator.locks.request === "function"
  );
}

/**
 * The network refresh call + store update — the one place that calls the
 * backend and writes the result.
 */
async function doNetworkRefresh(
  currentRefreshToken: string | null | undefined,
): Promise<RefreshResult> {
  // The session this refresh renews. A sign-out — or a sign-in as someone
  // else — while it is on the wire (a background retry on a slow link can
  // take up to REFRESH_REQUEST_TIMEOUT_MS) leaves its answer belonging to a
  // session this tab has left; applying it would sign the previous user
  // straight back in on a shared back-office PC. A refresh sent signed out
  // (AuthInitializer's cookie refresh at boot) has nothing to outlive.
  const sessionTokenAtSend = useAuthStore.getState().token;
  const sentSeq = issueRequestSeq();

  let result: RefreshResult;
  try {
    result = await AuthService.refreshSession({
      refreshToken: currentRefreshToken || undefined,
    });
  } catch (refreshError) {
    // INV-RC-17 — the refresh endpoint answering "temporarily unavailable"
    // (503 SESSION_STORE_UNAVAILABLE, a gateway error, no answer) is the
    // session store being down, NOT the session being over. Record it for the
    // banner and the recovery probe; the caller's transient-failure path
    // (isTerminalRefreshFailure) keeps the session. A tab with no session of
    // its own (the boot cookie refresh) has nothing to keep and shows nothing.
    const outage = classifyServiceOutage(refreshError);
    if (outage && sessionTokenAtSend) {
      recordServiceUnavailable(outage, { sentSeq });
    }
    throw refreshError;
  }
  // A refresh the server answered proves the session store serves again.
  if (sessionTokenAtSend) recordServiceAvailable({ sentSeq });

  // #931 — a cookie refresh (sent signed out) whose answer lands after a
  // sign-out started: that session is being ended, never adopted.
  if (!sessionTokenAtSend && isRefreshBlockedByPendingLogout()) {
    throw new PendingLogoutError();
  }

  if (
    sessionTokenAtSend &&
    !isSameSession(sessionTokenAtSend, useAuthStore.getState().token)
  ) {
    throw new SessionSupersededError(
      "Signed out while the session was refreshing — the refreshed session was discarded.",
    );
  }

  if (result.accessToken) {
    useAuthStore.getState().setAuth({
      token: result.accessToken,
      refreshToken: result.refreshToken || currentRefreshToken,
    });

    if (result.expiresIn) {
      scheduleTokenRefresh(result.expiresIn);
    }
  }

  return result;
}

/**
 * Cross-tab-aware refresh, run inside the CROSS_TAB_REFRESH_LOCK Web Lock so
 * only one tab across the whole browser executes this at a time. By the
 * time a waiting tab acquires the lock, any tab that refreshed while it
 * waited has already written its new pair to localStorage, so the
 * freshness check below reliably sees it.
 *
 * Where Web Locks is unavailable (older Safari, non-secure context) this runs
 * unlocked: the persisted pair is still never staler than this tab's memory,
 * and two tabs that do send the same token at the same instant land inside
 * the backend's 45s grace window, which answers the second with the pair the
 * first just received.
 *
 * `staleAccessToken` is the token the caller wants replaced — see
 * refreshSessionDeduped.
 */
async function performCrossTabRefresh(
  staleAccessToken: string | null,
): Promise<RefreshResult> {
  const persisted = readPersistedAuthPair();
  const memory = useAuthStore.getState();

  // The storage listener normally catches a session switch first; this
  // covers a refresh that won the race against that event. Adopting or
  // rotating the other identity's pair here would hand it to this tab's
  // requests.
  if (
    persisted &&
    memory.token &&
    !isSameSession(memory.token, persisted.token)
  ) {
    supersedeTabSession(persisted.token);
    throw new SessionSupersededError();
  }

  // The newest pair known across tabs: the persisted one, unless storage went
  // backwards under a stale tab's write (isOlderRotation) — then this tab's
  // own pair is the newer one, and sending the persisted refresh token would
  // replay one that was already rotated away.
  const latest: PersistedAuthPair | null =
    persisted && isOlderRotation(persisted.token, memory.token)
      ? { token: memory.token, refreshToken: memory.refreshToken }
      : persisted;

  // Another tab already rotated past the token this caller holds. Adopt its
  // pair instead of rotating again — the latest refresh token is current,
  // so a second rotation wouldn't be a replay, but it would invalidate the
  // access token every other tab is using right now.
  if (
    latest?.token &&
    latest?.refreshToken &&
    latest.token !== staleAccessToken
  ) {
    const secondsLeft = decodeJwtExpirySeconds(latest.token);
    if (
      secondsLeft !== null &&
      secondsLeft > MIN_ADOPTABLE_TOKEN_LIFETIME_SECONDS
    ) {
      // Also rewrites storage when it had gone backwards.
      useAuthStore.getState().setAuth({
        token: latest.token,
        refreshToken: latest.refreshToken,
      });
      scheduleTokenRefresh(secondsLeft);
      return {
        accessToken: latest.token,
        refreshToken: latest.refreshToken,
        expiresIn: secondsLeft,
      };
    }
    // Latest access token is itself expired/undecodable — fall through to a
    // real refresh, but still using the LATEST refresh token (the freshest
    // one known across tabs), not this tab's possibly stale memory copy.
  }

  const currentRefreshToken = latest?.refreshToken ?? memory.refreshToken;
  return doNetworkRefresh(currentRefreshToken);
}

/**
 * Deduplicated refresh: returns the in-flight refresh promise if one exists,
 * otherwise starts a new one. On success, updates the auth store and schedules
 * the next proactive refresh.
 *
 * `staleAccessToken` is the access token the caller wants replaced — the one
 * a request 401'd with, or the one the proactive timer was scheduled for.
 * Defaults to this tab's current token. If another tab has already rotated
 * past it, the rotated pair is adopted instead of rotating again.
 */
export function refreshSessionDeduped(
  staleAccessToken?: string | null,
): Promise<RefreshResult> {
  if (sessionSupersededByAnotherTab) {
    return Promise.reject(new SessionSupersededError());
  }
  // #931 — every refresh path funnels through here: AuthInitializer's boot
  // cookie refresh, the 401 interceptor (a request that 401s signed out),
  // background retries. While a sign-out awaits server confirmation none of
  // them may run a cookie refresh.
  if (isRefreshBlockedByPendingLogout()) {
    return Promise.reject(new PendingLogoutError());
  }
  // GH-923 / Bug-143 — an impersonation session has no refresh token, so no
  // refresh can ever renew it. Letting one run anyway is worse than useless:
  // performCrossTabRefresh compares against the SHARED persisted pair (the
  // operator's own session), sees a different session and supersedes this
  // tab — reloading it, and on a reload it resumes support mode and can loop
  // — or, with nothing shared persisted, falls through to a cookie refresh
  // that rotates the operator's own session into this tab's store. The 401
  // interceptor already skips refresh for impersonation; this covers every
  // other caller (AuthInitializer's near-expiry check after a reload, the
  // proactive timer, onboarding's post-submit role refresh).
  if (isImpersonationSession()) {
    return Promise.reject(new ImpersonationSessionNotRefreshableError());
  }
  if (inflightRefresh) {
    return inflightRefresh;
  }

  const tokenToReplace =
    staleAccessToken === undefined
      ? useAuthStore.getState().token
      : staleAccessToken;

  inflightRefresh = (async () => {
    try {
      const refresh = () => performCrossTabRefresh(tokenToReplace);
      if (hasWebLocks()) {
        // Serializes across ALL tabs, not just this one — the per-tab
        // inflightRefresh guard above only dedupes callers within this tab.
        return await navigator.locks.request(CROSS_TAB_REFRESH_LOCK, refresh);
      }
      return await refresh();
    } finally {
      inflightRefresh = null;
    }
  })();

  return inflightRefresh;
}

/** setTimeout's largest delay (a signed 32-bit ms count); larger values fire at once. */
const MAX_TIMER_DELAY_MS = 2 ** 31 - 1;

export function scheduleTokenRefresh(expiresInSeconds: number) {
  clearTokenRefreshTimer();

  // The token this timer exists to replace. If another tab has rotated past
  // it by the time the timer fires, the refresh adopts that rotation rather
  // than rotating the session a second time.
  const scheduledForToken = useAuthStore.getState().token;

  // Refresh 2 minutes before expiry, with a minimum of 10 seconds — and a
  // maximum of setTimeout's own: past 2^31-1 ms (~24.8 days) the delay
  // overflows and the timer fires after ~1 ms. A real token never lives that
  // long; a client clock far behind the server's makes one look as if it does
  // (the help-centre capture's frozen clock read ~165 days), and every page
  // load then rotated the session at boot under requests still carrying the
  // old access token. Capped, a skewed tab refreshes reactively on the 401.
  const refreshInMs = Math.min(
    Math.max((expiresInSeconds - 120) * 1000, 10_000),
    MAX_TIMER_DELAY_MS,
  );

  refreshTimer = setTimeout(() => {
    // refreshSessionDeduped handles setAuth + rescheduling internally on
    // success. On failure, classify it (GH-924) rather than silently
    // swallowing: a terminal refusal ends the session now (nothing else is
    // waiting on this timer to notice); anything else is transient and gets
    // picked up by the background retry below.
    refreshSessionDeduped(scheduledForToken ?? undefined).catch((err) => {
      if (err instanceof SessionSupersededError) return; // this tab is reloading
      if (err instanceof PendingLogoutError) return; // #931 — signed out
      // GH-923 — a support-mode token can't be refreshed at all; it runs out
      // on its own. Not transient: retrying would loop until the tab closes.
      if (err instanceof ImpersonationSessionNotRefreshableError) return;
      if (isTerminalRefreshFailure(err)) {
        handleExpiredSession();
        return;
      }
      scheduleTransientRefreshRetry(scheduledForToken ?? undefined, err);
    });
  }, refreshInMs);
}

/**
 * GH-924(a) — called once at boot (root.tsx AuthInitializer) for a tab that
 * already holds a token from a previous session (a reload, or a new tab
 * inheriting a persisted session). Before this fix, a token with MORE than
 * 5 minutes of life left correctly skipped the immediate refresh round-trip
 * (avoiding an unwanted refresh-token rotation on every page reload — see
 * the historical note this replaced in root.tsx) but then never armed a
 * proactive timer either, so the tab ran with NO scheduled refresh until the
 * token actually expired and some request's reactive 401 triggered one.
 * Decodes `token`'s `exp` and arms the same "2 minutes before expiry" timer
 * a successful refresh would leave running.
 *
 * Returns false (and arms nothing) for a token that can't be decoded, or one
 * already inside the 5-minute refresh window — the caller refreshes
 * immediately instead in that case.
 */
export function resumeProactiveRefreshFromToken(token: string): boolean {
  const secondsLeft = decodeJwtExpirySeconds(token);
  if (secondsLeft === null || secondsLeft < 300) return false;
  scheduleTokenRefresh(secondsLeft);
  return true;
}

// ---------- Transient refresh retry (GH-924) ----------
//
// A refresh that fails for any reason other than a terminal refusal from the
// refresh endpoint (see isTerminalRefreshFailure) is the refresh
// infrastructure being temporarily unavailable — a Wi-Fi blip, the backend
// mid-deploy, a 429 — not proof the session itself is invalid. Logging out
// on any refresh failure (the pre-fix behavior) turned a transient network
// hiccup in ONE tab into every open tab signing out, via admin#922's
// cross-tab sync (handleExpiredSession's clearAuth() write propagates as a
// "session ended" storage event to every other tab). Instead: keep the
// session and retry with capped exponential backoff + jitter (never sooner
// than the server's retry hint — a 429's, or the 503 SESSION_STORE_UNAVAILABLE
// a session-store outage answers with, INV-RC-17). Each retry goes back through
// refreshSessionDeduped, so it re-reads the persisted pair and adopts
// another tab's rotation instead of sending this tab's token again.
//
// A retry re-sends the same refresh token even when the earlier send went
// unanswered and the outage has outlasted backend#1854's 45s grace — on
// purpose. An unanswered send either never rotated (the usual case: the
// request never reached the API) and the retry simply succeeds, or it
// rotated and only the response was lost. In that second case the new pair
// reached no tab, and the backend only accepts a session's CURRENT access
// token, so the session is already unusable from this browser; the late
// replay's reuse revocation (401 → handleExpiredSession) ends nothing that
// was still alive. Refusing the replay locally instead would sign the user
// out in both cases — every upstream outage longer than the grace that
// overlapped a refresh — for no gain. Inside the grace a replay of a
// rotated token is served the current pair, and REFRESH_REQUEST_TIMEOUT_MS
// (auth.ts) keeps the first retries inside it.
let transientRefreshRetryTimer: ReturnType<typeof setTimeout> | null = null;
let transientRefreshRetryAttempt = 0;

const TRANSIENT_REFRESH_RETRY_BASE_MS = 5_000;
const TRANSIENT_REFRESH_RETRY_MAX_MS = 60_000;
// Ceiling on a server-sent Retry-After, so a misconfigured header can't park
// the retry for hours.
const TRANSIENT_REFRESH_RETRY_AFTER_MAX_MS = 5 * 60_000;

/** Fired on `window` whenever a transient refresh retry starts/stops, so any
 *  listener can follow it without this module knowing who listens — mirrors
 *  the existing `billing:feature-blocked` event pattern. The "Service
 *  temporarily unavailable" banner reads ~/lib/service-availability instead,
 *  which also covers outages the refresh never sees (INV-RC-17). */
const REFRESH_CONNECTIVITY_EVENT = "auth:refresh-connectivity";

function dispatchRefreshConnectivity(offline: boolean): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent(REFRESH_CONNECTIVITY_EVENT, { detail: { offline } }),
  );
}

export function clearTransientRefreshRetry(): void {
  if (transientRefreshRetryTimer !== null) {
    clearTimeout(transientRefreshRetryTimer);
    transientRefreshRetryTimer = null;
    // A cancelled retry never reaches its own "back online" dispatch.
    dispatchRefreshConnectivity(false);
  }
  transientRefreshRetryAttempt = 0;
}

/** True while a transient refresh failure is being retried in the
 *  background — exposed for a future "reconnecting" indicator; nothing
 *  reads this today (the CustomEvent above is the live signal). */
export function isRefreshRetryPending(): boolean {
  return transientRefreshRetryTimer !== null;
}

/** Milliseconds the refresh endpoint asked us to wait (capped), or 0 when it
 *  gave no hint. `retryAfterMsOf` reads the `Retry-After` header and falls
 *  back to the error body's `retryAfter` — the header is invisible to this
 *  cross-origin app (the API doesn't expose it via CORS), so for a
 *  503 SESSION_STORE_UNAVAILABLE the body is where the hint actually arrives
 *  (INV-RC-17). `refreshError` is handleApiError's wrapper; both helpers read
 *  through to the axios error it wraps. */
function refreshRetryAfterMs(refreshError: unknown): number {
  const asked = retryAfterMsOf(refreshError);
  return asked === null
    ? 0
    : Math.min(asked, TRANSIENT_REFRESH_RETRY_AFTER_MAX_MS);
}

function scheduleTransientRefreshRetry(
  staleAccessToken: string | null | undefined,
  refreshError?: unknown,
): void {
  if (transientRefreshRetryTimer !== null) return; // already retrying

  transientRefreshRetryAttempt += 1;
  const backoff = Math.min(
    TRANSIENT_REFRESH_RETRY_BASE_MS * 2 ** (transientRefreshRetryAttempt - 1),
    TRANSIENT_REFRESH_RETRY_MAX_MS,
  );
  const jitterMs = Math.random() * backoff * 0.3;
  const delayMs = Math.max(backoff + jitterMs, refreshRetryAfterMs(refreshError));

  dispatchRefreshConnectivity(true);

  transientRefreshRetryTimer = setTimeout(() => {
    transientRefreshRetryTimer = null;
    // Signed out since this was scheduled (every sign-out path clears the
    // retry, but a store cleared by other means must never be "refreshed"
    // back into a session off the HttpOnly cookie).
    if (!useAuthStore.getState().token) {
      transientRefreshRetryAttempt = 0;
      dispatchRefreshConnectivity(false);
      return;
    }
    refreshSessionDeduped(staleAccessToken).then(
      () => {
        transientRefreshRetryAttempt = 0;
        dispatchRefreshConnectivity(false);
      },
      (err) => {
        if (err instanceof SessionSupersededError) return; // this tab is reloading
        if (err instanceof PendingLogoutError) {
          // #931 — signed out; there is no session left to keep alive.
          transientRefreshRetryAttempt = 0;
          dispatchRefreshConnectivity(false);
          return;
        }
        if (err instanceof ImpersonationSessionNotRefreshableError) {
          transientRefreshRetryAttempt = 0;
          dispatchRefreshConnectivity(false);
          return;
        }
        if (isTerminalRefreshFailure(err)) {
          transientRefreshRetryAttempt = 0;
          dispatchRefreshConnectivity(false);
          handleExpiredSession();
          return;
        }
        scheduleTransientRefreshRetry(staleAccessToken, err);
      },
    );
  }, delayMs);
}

/**
 * Cross-tab sync (backend#1854): another tab's write to the persisted auth
 * store fires a `storage` event in every OTHER tab (browsers never fire it
 * in the tab that made the write).
 *  - Same session, new pair (another tab refreshed): take the token pair so
 *    this tab's next request carries the current access token — the backend
 *    rejects every earlier one. ONLY the pair: selected location, profile and
 *    the rest stay per-tab, so switching location in one tab never re-scopes
 *    another tab mid-edit.
 *  - Different session (logout, a different user signing in, an
 *    impersonation handoff): supersedeTabSession — detach and reload rather
 *    than mixing identities in one page.
 */
export function handleAuthStorageEvent(event: Pick<StorageEvent, "key">) {
  if (event.key !== AUTH_STORAGE_KEY) return;
  if (sessionSupersededByAnotherTab) return;

  // GH-923 — this tab is running an Ops Panel impersonation session, scoped
  // to its own sessionStorage key (scopeAuthStoreToImpersonationTab). It
  // never writes AUTH_STORAGE_KEY itself anymore, but another tab's OWN
  // write to it (e.g. the operator's proactive refresh rotating their own
  // token in a different tab) still reaches this listener. Without this
  // guard, this tab would see "a different session in storage" and
  // supersede itself into the operator's identity — reloading out of
  // support mode mid-session, from the wrong side.
  if (isImpersonationSession()) return;

  const persisted = readPersistedAuthPair();
  const nextToken = persisted?.token ?? null;
  const {
    token: currentToken,
    refreshToken: currentRefreshToken,
    setAuth,
  } = useAuthStore.getState();

  if (!isSameSession(currentToken ?? null, nextToken)) {
    supersedeTabSession(nextToken);
    return;
  }

  // Skip when signed out on both sides, or when nothing changed — adopting
  // writes this tab's state back to storage, and that echo must not bounce
  // between tabs forever.
  if (
    !persisted?.token ||
    (persisted.token === currentToken &&
      persisted.refreshToken === currentRefreshToken)
  ) {
    return;
  }

  // Storage went backwards (see isOlderRotation): keep this tab's newer pair
  // and write it back, so the tab that regressed it adopts it in turn.
  if (currentToken && isOlderRotation(persisted.token, currentToken)) {
    setAuth({ token: currentToken, refreshToken: currentRefreshToken });
    return;
  }
  setAuth({ token: persisted.token, refreshToken: persisted.refreshToken });
}

let crossTabSyncCleanup: (() => void) | null = null;

/**
 * Wires handleAuthStorageEvent to the browser's `storage` event, and to
 * `pageshow` for a page restored from the back/forward cache. Mounted once
 * from root.tsx (see <CrossTabAuthSync />). Idempotent — a second call
 * returns the existing cleanup function instead of double-registering.
 */
export function initCrossTabAuthSync(): () => void {
  if (typeof window === "undefined") {
    return () => {};
  }
  if (crossTabSyncCleanup) {
    return crossTabSyncCleanup;
  }

  const listener = (event: StorageEvent) => handleAuthStorageEvent(event);
  // A page restored from the back/forward cache missed every storage event
  // fired while it was cached, so it can wake holding a pair another tab has
  // long since rotated away, or a session that has since ended. Reconcile
  // before it does anything else — its first store write (a location switch,
  // a /me refetch) would otherwise persist that stale pair for every tab.
  const onPageShow = (event: PageTransitionEvent) => {
    if (event.persisted) handleAuthStorageEvent({ key: AUTH_STORAGE_KEY });
  };
  window.addEventListener("storage", listener);
  window.addEventListener("pageshow", onPageShow);
  crossTabSyncCleanup = () => {
    window.removeEventListener("storage", listener);
    window.removeEventListener("pageshow", onPageShow);
    crossTabSyncCleanup = null;
  };
  return crossTabSyncCleanup;
}

const PUBLIC_AUTH_PATHS = [
  "/login",
  "/auth/mfa-challenge",
  "/signup",
  "/verify-email",
  "/forgot-password",
  "/reset-password",
  "/accept-invite",
  "/invite",
];

/**
 * GH-924 — true when a refresh failure proves this session can't be
 * refreshed from here, so it ends. Everything else is the refresh
 * infrastructure being temporarily unavailable and must NOT log the user out.
 *
 * Terminal:
 *  - 401/403 from the refresh endpoint — a revoked, invalid or expired
 *    refresh token, or a disabled account.
 *  - Any other 4xx except 408 and 429 (a 400 for a malformed token, a 404):
 *    the server understood and refused; the identical request would be
 *    refused on every retry, leaving the tab retrying forever while every
 *    call 401s.
 * Transient: no response (`statusCode: 0` — offline, DNS, a dropped
 * connection or client timeout), 408, 429 and every 5xx.
 *
 * `AuthService.refreshSession` throws via `handleApiError` (app/lib/utils.ts),
 * which carries the HTTP status as `.statusCode`.
 */
function isTerminalRefreshFailure(error: unknown): boolean {
  const statusCode = (error as { statusCode?: unknown } | undefined)
    ?.statusCode;
  if (typeof statusCode !== "number") return false;
  return (
    statusCode >= 400 &&
    statusCode < 500 &&
    statusCode !== 408 &&
    statusCode !== 429
  );
}

/**
 * TC-SESSION-004 — a 401 raised because the account itself was disabled (the
 * backend's JwtStrategy.validate rejects a DISABLED user with
 * `UnauthorizedException("Account is disabled")` on every request once an admin
 * terminates the linked employee). This is NOT a refreshable token expiry: the
 * backend also revoked every Redis session, so a refresh would fail anyway. We
 * detect it explicitly and short-circuit straight to logout — both to avoid the
 * wasted refresh round-trip and so the force-logout doesn't depend on a
 * (possibly misbehaving) backend still rejecting the refresh token.
 */
function isDisabledAccount401(error: unknown): boolean {
  const body = (error as { response?: { data?: unknown } })?.response?.data as
    | { message?: unknown; error?: unknown; code?: unknown; details?: { code?: unknown } }
    | undefined;
  if (!body || typeof body !== "object") return false;

  const code =
    (typeof body.code === "string" && body.code) ||
    (typeof body.details?.code === "string" && body.details.code) ||
    "";
  if (code === "AUTH_003") return true;

  const text = [body.message, body.error]
    .filter((v): v is string => typeof v === "string")
    .join(" ")
    .toLowerCase();
  return (
    text.includes("account is disabled") ||
    text.includes("account has been disabled")
  );
}

/**
 * Bug-143 — a super-admin acting via Ops Panel impersonation carries an
 * impersonation JWT that `root.tsx`'s AuthInitializer stores WITHOUT a
 * refresh token (there is no refresh flow for impersonation sessions — they
 * expire on their own short TTL). If an impersonated request 401s for any
 * reason, the refresh branch below would call refreshSessionDeduped() with no
 * refreshToken, which fails, which force-logs-out the super-admin entirely —
 * even though their real admin session is unaffected. That's the bug: a
 * single failed write during impersonation should surface as a request error
 * (toast, handled by the calling mutation's onError), not tear down the
 * session.
 *
 * Detection mirrors <ImpersonationBanner /> (app/components/shared/
 * impersonation-banner.tsx): decode the current token's `type` claim rather
 * than adding a new store flag, since the JWT itself is the source of truth
 * for "is this session an impersonation session."
 *
 * WHY the impersonated menu-item write 401s in the first place is a separate,
 * backend-side issue tracked for live repro — out of scope here. This only
 * stops the logout side-effect.
 */
function isImpersonationSession(): boolean {
  const token = useAuthStore.getState().token;
  if (!token) return false;
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return false;
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const json = atob(base64);
    const payload = JSON.parse(json) as { type?: unknown };
    return payload?.type === "impersonation";
  } catch {
    return false;
  }
}

/** The Bearer token a request was sent with, or undefined when it had none. */
function bearerTokenOf(headers: unknown): string | undefined {
  const value = (headers as { Authorization?: unknown } | undefined)
    ?.Authorization;
  return typeof value === "string" && value.startsWith("Bearer ")
    ? value.slice("Bearer ".length)
    : undefined;
}

// Called when a 401 response can't be recovered via refresh. Clears auth state
// and sends the user to the login page — no modal, no overlay. Guards against
// looping when the user is already on a public auth route.
function handleExpiredSession() {
  clearTokenRefreshTimer();

  // Another tab replaced this session and this tab is already reloading —
  // clearAuth here would sign that other session out of every tab.
  if (sessionSupersededByAnotherTab) return;

  // INV-RC-17 — the session is over, so there is nothing left to keep alive
  // through an outage: drop its banner and stop probing.
  resetServiceAvailability();

  const wasAuthenticated = Boolean(useAuthStore.getState().token);
  if (!wasAuthenticated) return;

  useAuthStore.getState().clearAuth();

  if (typeof window === "undefined") return;
  const path = window.location.pathname;
  const onAuthRoute = PUBLIC_AUTH_PATHS.some(
    (p) => path === p || path.startsWith(`${p}/`),
  );
  if (!onAuthRoute) {
    window.location.assign("/login");
  }
}

// Reject every request immediately when the browser reports offline so forms
// surface "no connection" instead of spinning until the 45-second timeout.
function rejectIfOffline(): Promise<never> | null {
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    const err = new axios.AxiosError(
      "No internet connection. Check your connection and try again.",
      "ERR_NETWORK",
    );
    return Promise.reject(err);
  }
  return null;
}

/** A request config stamped with its dispatch's send order (INV-RC-17). */
type AvailabilityStamped = { _availabilitySeq?: number };

axiosPrivate.interceptors.request.use(
  (request) => {
    const offline = rejectIfOffline();
    if (offline) {
      // INV-RC-17 — offline is the same "temporarily unavailable" state: a
      // signed-in tab shows the banner and probes (at once on `online`)
      // instead of treating the failed calls as the end of anything.
      if (useAuthStore.getState().token) {
        recordServiceUnavailable({
          kind: "offline",
          status: null,
          code: null,
          message: null,
          retryAfterMs: null,
        });
      }
      return offline as unknown as typeof request;
    }

    // Skip adding Authorization header for OPTIONS requests (CORS preflight)
    if (request.method?.toUpperCase() === "OPTIONS") {
      return request;
    }

    // Get token from Zustand store
    const token = useAuthStore.getState().token;

    if (token) {
      request.headers.Authorization = `Bearer ${token}`;
    }

    // INV-RC-17 — this dispatch's send order (a replay after a refresh draws a
    // fresh one), so a late answer can never overrule a newer one.
    (request as AvailabilityStamped)._availabilitySeq = issueRequestSeq();

    // DEV-FA7 — echo the palate_csrf cookie on mutating requests.
    attachCsrfHeader(request as never);

    return request;
  },
  (error) => Promise.reject(error),
);

axiosInstance.interceptors.request.use(
  (request) => {
    const offline = rejectIfOffline();
    if (offline) return offline as unknown as typeof request;

    // DEV-FA7 — the public instance is built by the same factory, so it also
    // carries `withCredentials: true` and sends the session cookie; the backend
    // skips CSRF only when that cookie is absent. Six non-skip-listed auth
    // endpoints go out through here, so they need the header too. No cookie
    // (login/signup) means readCsrfToken() returns null and nothing is set.
    attachCsrfHeader(request as never);

    return request;
  },
  (error) => Promise.reject(error),
);

axiosPrivate.interceptors.response.use(
  (response) => {
    // INV-RC-17 — an authenticated answer proves the session store serves: it
    // ends any outage (the banner goes, and the reads that failed re-run).
    if (bearerTokenOf(response.config?.headers)) {
      recordServiceAvailable({
        sentSeq: (response.config as AvailabilityStamped | undefined)
          ?._availabilitySeq,
      });
    }
    return response;
  },
  async (error) => {
    const originalRequest = error?.config;

    if (!originalRequest) {
      return Promise.reject(error);
    }

    // INV-RC-17 — "temporarily unavailable" (503 SESSION_STORE_UNAVAILABLE, a
    // gateway 502/504, no answer at all) is NOT an authentication failure.
    // Record it for the banner and the recovery probe, and let THIS request
    // fail with the server's own message. Nothing here touches the session:
    // only the 401 branches below sign out, and only on a real refusal.
    const outage = classifyServiceOutage(error);
    if (outage && bearerTokenOf(originalRequest.headers)) {
      recordServiceUnavailable(outage, {
        sentSeq: (originalRequest as AvailabilityStamped)._availabilitySeq,
      });
    }

    let message =
      error?.response?.data?.error ||
      error?.response?.data?.message ||
      error?.response?.data?.detail ||
      error?.response?.data?.non_field_errors?.[0] ||
      error?.message ||
      "Network Error";

    // Handle timeout specifically
    if (error.code === "ECONNABORTED" && error.message.includes("timeout")) {
      message =
        "Request timed out. Please check your internet connection or try again later.";
      // Assign the new message to the error object so downstream handlers (like toasts) see it
      error.message = message;
    }

    // Network failure (offline, DNS, server unreachable) — surface a clear message
    if (
      error.code === "ERR_NETWORK" ||
      error.message === "Network Error" ||
      (typeof navigator !== "undefined" && navigator.onLine === false)
    ) {
      message = "No internet connection. Check your connection and try again.";
      error.message = message;
    }

    const status = error?.response?.status;

    // XD-006 — Parse 422 STRIPE_GATE_CLOSED responses into a normalized shape
    // so callers can react with status-specific UX (deep-link toast, disabled
    // toggle copy) without re-parsing the response body at every call site.
    // Sprint 9 delta § B.1: the response carries { code, status, messageKey,
    // settingsDeepLinkPath }.
    if (status === 422) {
      const body = error?.response?.data;
      if (body && typeof body === "object" && body.code === "STRIPE_GATE_CLOSED") {
        error.stripeGate = {
          code: "STRIPE_GATE_CLOSED",
          status: body.status,
          messageKey: body.messageKey,
          settingsDeepLinkPath: body.settingsDeepLinkPath,
        };
      }
    }

    // XD-017-J — Parse 403 FEATURE_NOT_AVAILABLE responses into a normalized
    // shape and broadcast a window event so the global <UpgradePromptPortal />
    // can surface the paywall dialog from any failed call. Subscription
    // Billing dev-spec § 6 + design-doc § 12.
    //
    // The backend's error envelope nests everything but statusCode / error /
    // message under `details` — `{ error: "FeatureNotAvailable", details:
    // { code, requiredFeature, upgradeUrl } }` — so reading `code` at the top
    // level never matched a real response and the dialog never opened; the
    // owner got a bare toast (preflight admin/X1 BILL-FGT-004, 2026-09-23).
    // Top-level fields are still honoured.
    if (status === 403) {
      const body = error?.response?.data;
      const gate =
        body && typeof body === "object"
          ? { ...body, ...(typeof body.details === "object" ? body.details : {}) }
          : null;
      if (gate && gate.code === "FEATURE_NOT_AVAILABLE") {
        const featureKey: string =
          gate.requiredFeature ?? gate.feature ?? "unknown";
        error.featureGate = {
          code: "FEATURE_NOT_AVAILABLE",
          requiredFeature: featureKey,
          upgradeUrl: gate.upgradeUrl ?? "/settings/billing",
        };
        if (typeof window !== "undefined") {
          window.dispatchEvent(
            new CustomEvent("billing:feature-blocked", {
              detail: {
                featureKey,
                upgradeUrl: error.featureGate.upgradeUrl,
              },
            }),
          );
        }
      }
    }

    // DEV-FA4 — a @Sudo-guarded route returns 403 { error: "sudo_required" }
    // when the session isn't elevated. Open the shared sudo modal; on a correct
    // password retry the original request once (re-running the interceptor chain
    // re-attaches auth + CSRF). Concurrent sudo_required failures share one
    // modal — useSudoStore queues every waiter and settles them together.
    if (
      status === 403 &&
      error?.response?.data?.error === "sudo_required" &&
      !originalRequest._sudoRetry
    ) {
      originalRequest._sudoRetry = true;
      const elevated = await useSudoStore.getState().requestElevation();
      if (elevated) {
        return axiosPrivate.request(originalRequest);
      }
      return Promise.reject(error);
    }

    // TC-SESSION-004 — a hard 401 because the account was disabled mid-session.
    // Not refreshable (sessions are revoked server-side), so skip the refresh
    // attempt entirely and force-logout immediately: clear auth + redirect to
    // /login. handleExpiredSession is a no-op on public auth routes (no loop).
    if (status === 401 && isDisabledAccount401(error)) {
      handleExpiredSession();
      return Promise.reject(error);
    }

    // Bug-143 — an impersonation session has no refresh token by design (see
    // isImpersonationSession above). Attempting the normal refresh→logout path
    // here would force-logout the super-admin over a single failed request.
    // Surface the error to the caller (existing onError/toast handling) and
    // leave the session intact instead.
    if (status === 401 && isImpersonationSession()) {
      return Promise.reject(error);
    }

    // Handle 401 errors — attempt silent token refresh. If refresh also fails,
    // the session is truly dead: clear auth state and send the user to /login
    // without any overlay. The redirect is a no-op when already on a public
    // auth route, so it can't loop.
    if (status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // The token this request actually carried: if another tab rotated
      // the session since (the usual reason for this 401), the refresh
      // adopts that rotation instead of rotating yet again. Also the token
      // a background retry (below) re-attempts against on a transient
      // failure.
      const sentWithToken = bearerTokenOf(originalRequest.headers);

      try {
        const result = await refreshSessionDeduped(sentWithToken);

        if (result.accessToken) {
          originalRequest.headers.Authorization = `Bearer ${result.accessToken}`;
          return axiosPrivate.request(originalRequest);
        }

        // Refresh resolved over the wire but returned no token — not an
        // explicit 401/403 auth rejection either. GH-924: treat the same as
        // a transient failure rather than assuming the session is dead.
        scheduleTransientRefreshRetry(sentWithToken);
        return Promise.reject(error);
      } catch (refreshError) {
        // Another tab already superseded this session (supersedeTabSession
        // is already mid-reload/redirect) — nothing to retry, and
        // handleExpiredSession is a no-op here anyway (guarded by the same
        // flag), so just propagate the rejection.
        if (
          refreshError instanceof SessionSupersededError ||
          // #931 — signed out; the request simply fails, nothing to retry.
          refreshError instanceof PendingLogoutError
        ) {
          return Promise.reject(refreshError);
        }

        // GH-924 — only a terminal refusal (isTerminalRefreshFailure) proves
        // the session itself is dead. A network error, timeout, 429, or 5xx
        // during refresh is the refresh infrastructure being temporarily
        // unavailable — keep the session, retry with backoff in the
        // background. THIS request still fails (the caller's existing
        // onError/toast handling surfaces that), but the session — and every
        // other open tab, via admin#922's cross-tab sync — survives.
        if (isTerminalRefreshFailure(refreshError)) {
          handleExpiredSession();
        } else {
          scheduleTransientRefreshRetry(sentWithToken, refreshError);
        }
        return Promise.reject(refreshError);
      }
    }

    // Log server errors (5xx, 404, 429) using logger utility — except a status the request itself
    // named as expected (see `expectedStatuses`).
    const isExpectedStatus =
      Array.isArray(originalRequest.expectedStatuses) &&
      originalRequest.expectedStatuses.includes(status);
    if (status && shouldLogStatus(status) && !isExpectedStatus) {
      logError("API Error", error, {
        category: "api_error",
        data: {
          url: originalRequest.url,
          method: originalRequest.method,
          status,
          message,
        },
        tags: {
          error_type: "api_error",
          status_code: String(status),
        },
      });
    }

    return Promise.reject(error);
  },
);

axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    let message =
      error?.response?.data?.error ||
      error?.response?.data?.message ||
      error?.response?.data?.detail ||
      error?.response?.data?.non_field_errors?.[0] ||
      error?.message ||
      "Network Error";

    // Handle timeout specifically
    if (error.code === "ECONNABORTED" && error.message.includes("timeout")) {
      message =
        "Request timed out. Please check your internet connection or try again later.";
      error.message = message;
    }

    // Network failure (offline, DNS, server unreachable)
    if (
      error.code === "ERR_NETWORK" ||
      error.message === "Network Error" ||
      (typeof navigator !== "undefined" && navigator.onLine === false)
    ) {
      message = "No internet connection. Check your connection and try again.";
      error.message = message;
    }

    return Promise.reject(error);
  },
);

// ---------- Recovery probe (INV-RC-17) ----------
//
// While the API is temporarily unavailable, ask it again — on its own
// Retry-After, backing off while it gives none — with one lightweight
// authenticated read. The answer is recorded by the interceptors above like
// any other: a success ends the outage (and query.config.ts re-runs the reads
// that failed during it). An expired access token takes the normal 401 →
// refresh path, so the refresh that finally succeeds resumes the session with
// no sign-in and no reload. Nothing is probed for a tab with no session, or
// one another tab has superseded.
const SERVICE_PROBE_TIMEOUT_MS = 10_000;

configureServiceAvailabilityProbe({
  canProbe: () =>
    Boolean(useAuthStore.getState().token) && !sessionSupersededByAnotherTab,
  probe: () =>
    axiosPrivate.get("/v1/auth/me", { timeout: SERVICE_PROBE_TIMEOUT_MS }),
});

export default axiosInstance;
